'use client'
import { useEffect, useState } from 'react';
import CustomFetch from '@/Utils/APIRequest/AxiosInstance';
import { MarketingCampaignAPI } from '@/Utils/APIRequest/Endpoints';
import { useQuery } from '@tanstack/react-query';
import MarketingCampaignForm from '../MarketingCampaignForm';
import { useTranslation } from '@/Utils/Hooks/useTranslate';
import Loader from '@/Layout/Loader';
import PrimaryButton from '@/Component/Common/Forms/PrimaryButton';
import { LoaderSVG } from '@/Component/Common/SVGs/CommonSVG';

const MarketingCampaignDetail = (props) => {
    const { id } = props;
    const { translations } = useTranslation();
    const [showEditForm, setShowEditForm] = useState(false);

    // API GET
    const { data, isPending, fetchStatus, refetch } = useQuery({
        queryKey: [MarketingCampaignAPI, id],
        queryFn: () => CustomFetch({ url: `${MarketingCampaignAPI}/details/${id}` }, null, false),
        refetchOnWindowFocus: false,
        select: (data) => data?.data
    });

    useEffect(() => {
        if (data?.details?.status === 'draft') {
            const isDraft = campaign?.status === 'draft';
            setShowEditForm(true);
        }
    }, [data]);

    const campaign = data?.details;

    const handleFormClose = (result) => {
        setShowEditForm(false);
        if (result) {
            refetch(); // refresh the campaign data after update
        }
    };

    if (isPending) {
        return <Loader />;
    }

    if (!campaign) {
        return <div className='p-6'>{translations?.not_found || 'Campaign not found'}</div>;
    }

    // If status is draft, show edit form
    if (showEditForm) {
        return (
            <MarketingCampaignForm
                initialCampaigns={[campaign]}
                onClose={handleFormClose}
                isEditMode={true}
                campaignId={id}
            />
        );
    }

    const STATS_CONFIG = [
        { key: "requests", label: "EMAILS TRIGGERED", color: "text-green-600" },
        { key: "delivered", label: "DELIVERED", color: "text-green-500" },
        { key: "opens", label: "TOTAL OPENS", color: "text-purple-500" },
        { key: "unique_opens", label: "UNIQUE OPENS", color: "text-purple-700" },
        { key: "clicks", label: "TOTAL CLICKS", color: "text-yellow-600" },
        { key: "unique_clicks", label: "UNIQUE CLICKS", color: "text-yellow-700" },
        { key: "bounces", label: "BOUNCES", color: "text-red-600" },
        { key: "bounce_drops", label: "BOUNCE DROPS", color: "text-red-400" },
        { key: "invalid_emails", label: "INVALID EMAILS", color: "text-red-500" },
        { key: "unsubscribes", label: "UNSUBSCRIBES", color: "text-pink-600" },
        { key: "spam_reports", label: "SPAM REPORTS", color: "text-pink-700" },
        { key: "spam_report_drops", label: "SPAM REPORT DROPS", color: "text-pink-500" },
    ];

    return (
        <div>
            <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>{campaign?.name}</h2>
                <PrimaryButton onClick={refetch} disabled={fetchStatus === 'fetching'} customClass='flex items-center justify-center gap-2'>
                    Refresh
                    {fetchStatus === 'fetching' && <LoaderSVG />}
                </PrimaryButton>
            </div>
            <div className='grid 2xl:grid-cols-6 xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 grid-cols-2 gap-2 border border-lightGray rounded-xl p-5'>
                {STATS_CONFIG.map(item => {
                    const value = data?.stats?.stats?.[item.key] ?? 0;

                    return (
                        <div key={item.key} className="flex flex-col items-start p-4">
                            <div className={`text-2xl font-bold ${item.color}`}>
                                {value}
                            </div>
                            <div className="text-sm font-medium text-gray-500 uppercase text-left">
                                {item.label}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MarketingCampaignDetail;
